// DateTimeFormatter.base.ts
// Shared types, constants and formatting helpers. Platform-specific implementations live in:
//   DateTimeFormatter.ts     (native / React Native)
//   DateTimeFormatter.web.ts (web / browser)

/** Shape returned by api/users/details for the user's timezone. */
export interface UserTimezone {
  keyName?: string;
  timeZoneID?: number;
  displayName?: string;
}

export type DateFormatMode =
  | 'date'
  | 'dateTime'
  | 'dateTimeWithSeconds'
  | 'dateWithYear'
  | 'dateTimeWithYear'
  | 'dateTimeYearWithSeconds'
  /** MM/DD/YYYY HH:MM AM/PM  (e.g. "10/13/2026 02:45 PM") */
  | 'shortDateTime';

// ── Shared constants ─────────────────────────────────────────────────────────

export const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

// ── Shared utilities ─────────────────────────────────────────────────────────

/**
 * Parses the UTC offset (in minutes) encoded in a Windows timezone displayName.
 * e.g. "(GMT-05:00) Eastern Time (US & Canada)" → -300
 *      "(GMT+05:30) Chennai, Kolkata, Mumbai, New Delhi" → 330
 */
export const parseOffsetMinutes = (displayName: string): number => {
  const match = /GMT([+-])(\d{2}):(\d{2})/.exec(displayName);
  if (!match) return 0;
  const sign  = match[1] === '+' ? 1 : -1;
  const hours = Number.parseInt(match[2] ?? '0', 10);
  const mins  = Number.parseInt(match[3] ?? '0', 10);
  return sign * (hours * 60 + mins);
};

// ── Shared date-rendering helpers ────────────────────────────────────────────

/** Pre-computed date/time components passed to applyDateMode. */
interface DateParts {
  mon: string;
  /** Zero-padded numeric month, e.g. "01"–"12". Used by the shortDateTime mode. */
  monthNum: string;
  day: string;
  yr: number | string;
  h12: string;
  min: string;
  sec: string;
  ampm: string;
}

/** Applies the DateFormatMode template to pre-computed date/time components. */
const applyDateMode = (parts: DateParts, mode: DateFormatMode): string => {
  const {mon, monthNum, day, yr, h12, min, sec, ampm} = parts;
  switch (mode) {
    case 'date':                    return `${mon} ${day}`;
    case 'dateTimeWithSeconds':     return `${mon} ${day}, ${h12}:${min}:${sec} ${ampm}`;
    case 'dateWithYear':            return `${mon} ${day}, ${yr}`;
    case 'dateTimeWithYear':        return `${mon} ${day}, ${yr} ${h12}:${min} ${ampm}`;
    case 'dateTimeYearWithSeconds': return `${mon} ${day}, ${yr} ${h12}:${min}:${sec} ${ampm}`;
    case 'shortDateTime':           return `${monthNum}/${day}/${yr} ${h12}:${min} ${ampm}`;
    case 'dateTime':
    default:                        return `${mon} ${day}, ${h12}:${min} ${ampm}`;
  }
};

/**
 * Formats `utcMs` using the given IANA timezone via `Intl.DateTimeFormat`.
 *
 * Returns the formatted string on success, or `null` if the `Intl` API throws
 * (which signals the caller to fall through to the static-offset path).
 *
 * Both platform files call this function after resolving their respective
 * IANA zone — the rendering logic is identical on native and web.
 */
export const formatDateFromIana = (
  ianaZone: string,
  utcMs: number,
  mode: DateFormatMode,
): string | null => {
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: ianaZone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    });
    const parts = dtf.formatToParts(new Date(utcMs));
    const v = (t: string) => parts.find(p => p.type === t)?.value ?? '00';
    const h24     = Number.parseInt(v('hour'), 10) % 24;
    const min     = v('minute');
    const sec     = v('second');
    const day     = v('day');
    const yr      = v('year');
    const monthNum = v('month'); // already zero-padded by '2-digit' option
    const mon     = MONTHS[Number.parseInt(monthNum, 10) - 1] ?? '';
    const ampm    = h24 >= 12 ? 'PM' : 'AM';
    const h12     = String(h24 % 12 || 12).padStart(2, '0');
    return applyDateMode({mon, monthNum, day, yr, h12, min, sec, ampm}, mode);
  } catch {
    return null; // caller should fall through to static-offset path
  }
};

/**
 * Formats `utcMs` using a static UTC offset derived from the Windows timezone
 * displayName (no DST correction).
 *
 * Correct for non-DST zones (India, Arizona, Hawaii, China, etc.) and used as
 * the final fallback when IANA resolution fails or is not available.
 */
export const formatDateFromOffset = (
  utcMs: number,
  stdOffsetMin: number,
  mode: DateFormatMode,
): string => {
  const local    = new Date(utcMs + stdOffsetMin * 60_000);
  const day      = String(local.getUTCDate()).padStart(2, '0');
  const monthNum = String(local.getUTCMonth() + 1).padStart(2, '0');
  const mon      = MONTHS[local.getUTCMonth()] ?? '';
  const yr       = local.getUTCFullYear();
  const h24      = local.getUTCHours();
  const min      = String(local.getUTCMinutes()).padStart(2, '0');
  const sec      = String(local.getUTCSeconds()).padStart(2, '0');
  const ampm     = h24 >= 12 ? 'PM' : 'AM';
  const h12      = String(h24 % 12 || 12).padStart(2, '0');
  return applyDateMode({mon, monthNum, day, yr, h12, min, sec, ampm}, mode);
};